import AWS from 'aws-sdk';
import { api, VAULT_API_SUB_URLS } from '@/api';
import CryptoJS from 'crypto-js/core';
import 'crypto-js/lib-typedarrays';
import Base64 from 'crypto-js/enc-base64';
import MD5 from 'crypto-js/md5';

class S3Service {
  constructor() {
    this.SIGNATURE_VERSION = 'v4';
  }

  async putFile(fileData, fileExt) {
    // Get file policy for PUT to S3 bucket
    const FILE_POLICY = await this.requestFilePutPolicy(fileExt);

    // New s3 instance every time!
    const S3_INSTANCE = this.constructS3Instance(
      {
        accessKeyId: FILE_POLICY.data.accessKeyId,
        secretAccessKey: FILE_POLICY.data.secretAccessKey,
        sessionToken: FILE_POLICY.data.sessionToken,
      },
      FILE_POLICY.data.region,
    );

    // Construct params from file policy and file information
    const S3_PARAMS = await this.constructS3Params(
      FILE_POLICY.data.location,
      FILE_POLICY.data.bucket,
      fileData,
    );

    // Make request to S3 to put the file
    await this.s3PutObject(S3_INSTANCE, S3_PARAMS);

    return FILE_POLICY.data.imageId;
  }

  async getFile(fileUUID) {
    // Get file policy for GET to S3 bucket
    const FILE_POLICY = await this.requestFileGetPolicy(fileUUID);

    // New s3 instance every time!
    const S3_INSTANCE = this.constructS3Instance(
      {
        accessKeyId: FILE_POLICY.data.accessKeyId,
        secretAccessKey: FILE_POLICY.data.secretAccessKey,
        sessionToken: FILE_POLICY.data.sessionToken,
      },
      FILE_POLICY.data.region,
    );

    // Construct params from file policy and file information
    const S3_PARAMS = await this.constructS3Params(
      FILE_POLICY.data.location,
      FILE_POLICY.data.bucket,
    );

    // Make request to S3 to get the file
    const FILE_DATA = await this.s3GetObject(S3_INSTANCE, S3_PARAMS);

    return new Blob([FILE_DATA.Body]);
  }

  // Create a new S3 instance with the credentials we're given
  constructS3Instance({ accessKeyId, secretAccessKey, sessionToken }, region) {
    const S3_INSTANCE = new AWS.S3({
      endpoint: process.env.VUE_APP_S3_ENDPOINT,
      correctClockSkew: true,
      signatureVersion: this.SIGNATURE_VERSION,
      region,
    });
    S3_INSTANCE.config.credentials = {
      accessKeyId,
      secretAccessKey,
      region,
      sessionToken,
    };
    return S3_INSTANCE;
  }

  requestFilePutPolicy(fileExt) {
    //Safari will cache subsequent requests if uploading multiple files
    const randomquery = Math.random();
    return api.get(`${VAULT_API_SUB_URLS.keys}/images/put-credentials`, {
      params: {
        fileExt,
        randomquery,
      },
    });
  }

  requestFileGetPolicy(fileUUID) {
    return api.get(`${VAULT_API_SUB_URLS.keys}/images/${fileUUID}/get-credentials`);
  }

  // Construct file-specific parameters
  async constructS3Params(fileKey, bucket, fileData) {
    const PARAMS = {
      Bucket: bucket,
      Key: fileKey,
    };
    if (fileData) {
      PARAMS.Body = fileData;

      await new Promise((res, rej) => {
        var reader = new FileReader();

        reader.addEventListener(
          'load',
          function () {
            var wordArray = CryptoJS.lib.WordArray.create(this.result);
            PARAMS.ContentMD5 = Base64.stringify(MD5(wordArray));
            res();
          }
        );

        reader.readAsArrayBuffer(fileData);
      });
    }
    return PARAMS;
  }

  s3PutObject(S3_INSTANCE, S3_PARAMS) {
    let bucketDestination;
    return S3_INSTANCE.putObject(S3_PARAMS)
      .on('sign', (r) => {
        bucketDestination = r.httpRequest.headers['Host'];
        r.httpRequest.headers['Host'] = `${S3_PARAMS.Bucket}.s3.us-east-2.amazonaws.com`;
      }, true) // boolean param here means run before the signing and next listener runs after signing is complete with false
      .on('sign', (r) => {
        r.httpRequest.headers['Host'] = bucketDestination;
      }, false)
      .promise();
  }

  s3GetObject(S3_INSTANCE, S3_PARAMS) {
    let bucketDestination;
    return S3_INSTANCE.getObject(S3_PARAMS)
      .on('sign', (r) => {
        bucketDestination = r.httpRequest.headers['Host'];
        r.httpRequest.headers['Host'] = `${S3_PARAMS.Bucket}.s3.us-east-2.amazonaws.com`;
      }, true)
      .on('sign', (r) => {
        r.httpRequest.headers['Host'] = bucketDestination;
      }, false)
      .promise();
  }
}

export const s3Instance = new S3Service(AWS);
